import { useSyncExternalStore } from "react";

/**
 * Toasts: a short message that outlives the control that raised it.
 *
 * For the one situation a screen cannot answer itself — a change whose effect
 * is somewhere other than where it was made. Setting a threshold on Low Stock
 * makes the row leave that list, and the row's own controls leave with it, so
 * the only place left to say so, and to offer the way back, is above the
 * screen rather than in it (#521).
 *
 * A module-level store rather than a context, so the code that raises one needs
 * no provider and can call it from a callback that outlives its component — the
 * Undo of a toast has to work after the row it came from has unmounted.
 * `<Toaster />` (mounted once, in the shell) is the only thing that draws them.
 */

/** A link beside the message: where the thing that changed can now be found. */
export interface ToastLink {
  label: string;
  to: string;
  search?: Record<string, string>;
}

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastInput {
  title: string;
  description?: string;
  /** The way back. Dismisses the toast when taken. */
  action?: ToastAction;
  link?: ToastLink;
  /** `error` is announced assertively; the rest are polite. */
  tone?: "default" | "error";
  /** How long it stays when nobody is pointing at it. Defaults to 10s. */
  durationMs?: number;
}

export interface Toast extends ToastInput {
  id: number;
}

/** More than this on screen at once is a wall; the oldest goes first. */
const MAX_VISIBLE = 3;

let toasts: readonly Toast[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function publish(next: readonly Toast[]): void {
  toasts = next;
  for (const listener of listeners) listener();
}

/** Show a toast. Returns its id, for `dismissToast`. */
export function showToast(input: ToastInput): number {
  const id = nextId;
  nextId += 1;
  publish([...toasts, { ...input, id }].slice(-MAX_VISIBLE));
  return id;
}

export function dismissToast(id: number): void {
  if (!toasts.some((toast) => toast.id === id)) return;
  publish(toasts.filter((toast) => toast.id !== id));
}

/**
 * Forget every toast, shown or not.
 *
 * For when the person using this tab changes. A toast is about what the person
 * who raised it just did — "Moved out of Low Stock", with an Undo that writes as
 * them — and it must not be there for the next one to read or press. Called
 * from `auth/session-cache.ts`, with the rest of what a change of person drops.
 */
export function clearToasts(): void {
  if (toasts.length === 0) return;
  publish([]);
}

/** The toasts held right now, for code outside React that has to know. */
export function currentToasts(): readonly Toast[] {
  return toasts;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): readonly Toast[] {
  return toasts;
}

export function useToasts(): readonly Toast[] {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
